/**
 * useFavorites — gerencia o conjunto de channel_ids favoritados pelo usuário
 * (Pacote 5). Cache em memória de módulo, compartilhado entre todas as
 * instâncias do hook (vários ChannelCards montados leem o mesmo Set).
 * Optimistic update no toggle, com rollback se a request falhar.
 *
 * Endpoints:
 *   GET    /api/nichos-virais/favorites          -> { channels: [...] }
 *   POST   /api/nichos-virais/favorites/{id}     -> favorita (idempotente)
 *   DELETE /api/nichos-virais/favorites/{id}     -> desfavorita
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API = (import.meta.env.VITE_BACKEND_URL || '') + '/api';

// Cache de módulo — sobrevive a remontagens e sincroniza todas as instâncias.
const _cachedFavoriteIds = new Set();

function _authHeaders() {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
}

async function fetchFavoriteIds() {
  const { data } = await axios.get(`${API}/nichos-virais/favorites`, {
    headers: _authHeaders(),
  });
  const channels = Array.isArray(data?.channels) ? data.channels : [];
  return channels.map((c) => c.channel_id).filter(Boolean);
}

export function useFavorites({ enabled = true } = {}) {
  const [favoriteIds, setFavoriteIds] = useState(() => new Set(_cachedFavoriteIds));

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    // Revalida na montagem (favoritos podem ter mudado em outra aba/sessão).
    // O cache em memória já aparece imediatamente nos cards.
    fetchFavoriteIds()
      .then((ids) => {
        if (cancelled) return;
        _cachedFavoriteIds.clear();
        ids.forEach((id) => _cachedFavoriteIds.add(id));
        setFavoriteIds(new Set(_cachedFavoriteIds));
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('Niche Finder: falha ao carregar favoritos:', err);
      });
    return () => { cancelled = true; };
  }, [enabled]);

  const toggleFavorite = useCallback(async (channelId) => {
    if (!channelId) return;
    const isFav = _cachedFavoriteIds.has(channelId);

    // Optimistic update (cache de módulo + state local).
    if (isFav) _cachedFavoriteIds.delete(channelId);
    else _cachedFavoriteIds.add(channelId);
    setFavoriteIds(new Set(_cachedFavoriteIds));

    try {
      if (isFav) {
        await axios.delete(`${API}/nichos-virais/favorites/${channelId}`, {
          headers: _authHeaders(),
        });
      } else {
        await axios.post(`${API}/nichos-virais/favorites/${channelId}`, null, {
          headers: _authHeaders(),
        });
      }
    } catch (err) {
      // Rollback em caso de erro.
      if (isFav) _cachedFavoriteIds.add(channelId);
      else _cachedFavoriteIds.delete(channelId);
      setFavoriteIds(new Set(_cachedFavoriteIds));
      // eslint-disable-next-line no-console
      console.error('Niche Finder: falha ao alternar favorito:', err);
    }
  }, []);

  const isFavorited = useCallback((id) => favoriteIds.has(id), [favoriteIds]);

  return { favoriteIds, toggleFavorite, isFavorited };
}
